import { useState, useRef } from 'react';
import API from './api';

const PRIORITY_STYLE = {
  'High 🔥':    { color: '#ff4d4d', bg: 'rgba(255,77,77,0.12)',   border: 'rgba(255,77,77,0.35)' },
  'Medium ⏳':  { color: '#f0a500', bg: 'rgba(240,165,0,0.12)',   border: 'rgba(240,165,0,0.35)' },
  'Low 💤':     { color: '#4caf7d', bg: 'rgba(76,175,125,0.12)',  border: 'rgba(76,175,125,0.35)' },
};

const TYPE_LABEL = {
  'action_item':     'Action Item',
  'meeting_request': 'Meeting',
  'follow_up':       'Follow-up',
  'information':     'Info',
};

export default function EmailAssistant({ setPage, onLogout }) {
  // Phase: 'upload' | 'results'
  const [phase, setPhase] = useState('upload');

  // Upload phase state
  const [file, setFile] = useState(null);
  const [emails, setEmails] = useState([]);
  const [toReviewCount, setToReviewCount] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Analysis phase state
  const [tasks, setTasks] = useState([]);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);

  // Notion send state: { [taskIndex]: { loading, success, error, url } }
  const [notionStatus, setNotionStatus] = useState({});
  const [showGuide, setShowGuide] = useState(false);

  const [error, setError] = useState('');

  // ── File handling ──────────────────────────────────────────────────────────

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.xlsx') &&
        !selectedFile.name.toLowerCase().endsWith('.xls')) {
      setError('Only .xlsx or .xls files are accepted.');
      return;
    }
    setError('');
    setFile(selectedFile);
    setEmails([]);
    setTasks([]);
    setNotionStatus({});
    setPhase('upload');

    // Preview — parse emails without Claude
    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      const res = await API.post('/email-assistant/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEmails(res.data.emails || []);
      setToReviewCount(res.data.to_review || 0);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      setError(e.response?.data?.detail || 'Could not read Excel file.');
      setFile(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  };

  // ── Analyse ────────────────────────────────────────────────────────────────

  const handleAnalyze = async () => {
    if (!file) return;
    setError('');
    setLoadingAnalyze(true);
    setTasks([]);
    setNotionStatus({});
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/email-assistant/analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTasks(res.data.tasks || []);
      setPhase('results');
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      setError(e.response?.data?.detail || 'Analysis failed. Check API key.');
    } finally {
      setLoadingAnalyze(false);
    }
  };

  // ── Notion send ────────────────────────────────────────────────────────────

  const sendTaskToNotion = async (taskOrTasks, indices) => {
    const taskList = Array.isArray(taskOrTasks) ? taskOrTasks : [taskOrTasks];
    const idxList  = Array.isArray(indices) ? indices : [indices];

    // Mark all as loading
    setNotionStatus(prev => {
      const next = { ...prev };
      idxList.forEach(i => { next[i] = { loading: true }; });
      return next;
    });

    try {
      const res = await API.post('/email-assistant/send-to-notion', { tasks: taskList });
      const results = res.data.results || [];
      setNotionStatus(prev => {
        const next = { ...prev };
        results.forEach((r, pos) => {
          const i = idxList[pos];
          next[i] = { loading: false, success: r.success, url: r.notion_url, error: r.error };
        });
        return next;
      });
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      setNotionStatus(prev => {
        const next = { ...prev };
        idxList.forEach(i => { next[i] = { loading: false, success: false, error: 'Request failed' }; });
        return next;
      });
    }
  };

  const handleSendAll = () => {
    const unsentIndices = tasks
      .map((_, i) => i)
      .filter(i => !notionStatus[i]?.success);
    if (unsentIndices.length === 0) return;
    const unsentTasks = unsentIndices.map(i => tasks[i]);
    sendTaskToNotion(unsentTasks, unsentIndices);
  };

  // ── Summary counts ─────────────────────────────────────────────────────────

  const highCount     = tasks.filter(t => t.priority === 'High 🔥').length;
  const meetingCount  = tasks.filter(t => t.type === 'meeting_request').length;
  const followUpCount = tasks.filter(t => t.type === 'follow_up').length;
  const sentCount     = Object.values(notionStatus).filter(s => s?.success).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={s.wrapper}>
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerTitle}>Email Assistant</div>
        <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
        <div style={s.headerBadge}>CMA CGM Hamburg Fleet</div>
      </header>

      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>Email Assistant — How to Guide</div>
                <div style={s.modalSub}>AI Email Action Item Extractor + Notion Sync</div>
              </div>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.gSection}>
                <div style={s.gTitle}>What is Email Assistant?</div>
                <div style={s.gText}>Email Assistant reads your <strong style={s.hl}>Power Automate Excel export</strong> from Outlook and uses Claude AI to extract every action item from your fleet emails. Tasks are then sent directly to your <strong style={s.hl}>Notion task management database</strong> with vessel linking, priority, and due date.</div>
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Setup: Power Automate Export</div>
                <div style={s.noteList}>
                  {['Configure a Power Automate flow to save incoming Outlook emails to an Excel file.',
                    'Required columns: To Review flag, From address, Subject, Date, Body (HTML is auto-stripped).',
                    'Only rows marked "To Review" are processed — all others are ignored.',
                  ].map((n,i) => <div key={i} style={s.noteItem}><span style={s.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Step-by-Step</div>
                {[
                  { n:'1', t:'Upload the Excel file', d:'Drag & drop or click to browse. Only .xlsx / .xls files accepted. The system previews how many "To Review" emails were found.' },
                  { n:'2', t:'Review the email list', d:'Confirm the emails shown match what you expect. The count shows how many will be sent to AI for analysis.' },
                  { n:'3', t:'Click Analyze with AI', d:'Claude reads every email and extracts all action items: task name, summary, priority, vessel, due date, key people.' },
                  { n:'4', t:'Review extracted tasks', d:'Each task card shows type (Action / Follow-up / Meeting / Info), priority (High/Medium/Low), and vessel name.' },
                  { n:'5', t:'Send to Notion', d:'Click the Notion button on individual tasks, or "Send All to Notion" for bulk send. Tasks are rate-limited (350ms between calls) to avoid API errors.' },
                ].map(step => (
                  <div key={step.n} style={s.step}>
                    <div style={s.stepN}>{step.n}</div>
                    <div><div style={s.stepT}>{step.t}</div><div style={s.gText}>{step.d}</div></div>
                  </div>
                ))}
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Fields Extracted per Task</div>
                <div style={s.noteList}>
                  {['Task name (max 70 chars), Summary (1 sentence)',
                    'Priority: High (safety/ISM/ISPS, deadline <48h), Medium (this week), Low (informational)',
                    'Category: Vessel Related / Office Related / Personal / Learning',
                    'Vessel name from fleet list (auto-linked to Notion vessel page)',
                    'Due date, Key people/organizations, Follow-up detail, Meeting request',
                  ].map((n,i) => <div key={i} style={s.noteItem}><span style={s.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main style={s.main}>

        {/* ── UPLOAD PHASE ── */}
        {phase === 'upload' && (
          <div style={s.uploadLayout}>

            {/* Drop zone */}
            <div
              style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={e => handleFileSelect(e.target.files[0])}
              />
              {!file ? (
                <>
                  <div style={s.dropIcon}>📧</div>
                  <div style={s.dropTitle}>Drop Excel file here</div>
                  <div style={s.dropSub}>or click to browse — .xlsx format from Power Automate</div>
                </>
              ) : (
                <>
                  <div style={s.dropIcon}>✅</div>
                  <div style={s.dropTitle}>{file.name}</div>
                  <div style={s.dropSub}>
                    {loadingPreview ? 'Loading emails…' : `${emails.length} rows found · ${toReviewCount} To Review`}
                  </div>
                </>
              )}
            </div>

            {/* Badge row */}
            {file && !loadingPreview && emails.length > 0 && (
              <div style={s.badgeRow}>
                <div style={s.badge}>📬 {emails.length} emails loaded</div>
                <div style={{ ...s.badge, ...s.badgeGold }}>🔍 {toReviewCount} To Review</div>
              </div>
            )}

            {error && <div style={s.error}>{error}</div>}

            {/* Email table */}
            {emails.length > 0 && !loadingPreview && (
              <div style={s.tableWrap}>
                <div style={s.tableLabel}>EMAIL PREVIEW</div>
                <div style={s.table}>
                  <div style={s.tableHeader}>
                    <span>FROM</span>
                    <span>SUBJECT</span>
                    <span>DATE</span>
                    <span>STATUS</span>
                  </div>
                  {emails.map((em, i) => (
                    <div key={i} style={{
                      ...s.tableRow,
                      opacity: em.status === 'To Review' ? 1 : 0.45,
                    }}>
                      <span style={s.mono}>{em.from_addr || '—'}</span>
                      <span style={s.subjectCell}>{em.subject || '—'}</span>
                      <span style={s.mono}>{em.received_date?.slice(0, 10) || '—'}</span>
                      <span>
                        <span style={{
                          ...s.statusBadge,
                          ...(em.status === 'To Review' ? s.statusReview : s.statusOther),
                        }}>
                          {em.status || '—'}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analyse button */}
            {file && !loadingPreview && toReviewCount > 0 && (
              <button
                style={{ ...s.analyzeBtn, opacity: loadingAnalyze ? 0.7 : 1 }}
                onClick={handleAnalyze}
                disabled={loadingAnalyze}
              >
                {loadingAnalyze ? '⏳  Analysing with Claude…' : `🤖  ANALYSE ${toReviewCount} EMAILS WITH CLAUDE`}
              </button>
            )}

            {loadingAnalyze && (
              <div style={s.analyzingBox}>
                <div style={s.analyzingIcon}>⚙️</div>
                <div style={s.analyzingText}>Claude is analysing emails…</div>
                <div style={s.analyzingSub}>Extracting action items, priorities and vessel references.</div>
              </div>
            )}
          </div>
        )}

        {/* ── RESULTS PHASE ── */}
        {phase === 'results' && (
          <div style={s.resultsLayout}>

            {/* Top bar */}
            <div style={s.resultsTopBar}>
              <div style={s.summaryRow}>
                <div style={s.summaryChip}>{tasks.length} Tasks</div>
                <div style={{ ...s.summaryChip, ...s.chipRed }}>🔥 {highCount} High</div>
                <div style={{ ...s.summaryChip, ...s.chipGold }}>📅 {meetingCount} Meetings</div>
                <div style={{ ...s.summaryChip, ...s.chipBlue }}>🔁 {followUpCount} Follow-ups</div>
                {sentCount > 0 && (
                  <div style={{ ...s.summaryChip, ...s.chipGreen }}>✓ {sentCount} Sent</div>
                )}
              </div>
              <div style={s.topBtnRow}>
                <button style={s.resetBtn} onClick={() => {
                  setPhase('upload'); setTasks([]); setNotionStatus({});
                }}>
                  ← New Upload
                </button>
                {tasks.length > 0 && (
                  <button style={s.sendAllBtn} onClick={handleSendAll}>
                    📤 Send All to Notion
                  </button>
                )}
              </div>
            </div>

            {tasks.length === 0 && (
              <div style={s.emptyResults}>
                <div style={s.emptyIcon}>📭</div>
                <div style={s.emptyText}>No action items found in the emails.</div>
                <div style={s.emptySub}>Only "To Review" emails with genuine actions are included.</div>
              </div>
            )}

            {/* Task cards */}
            <div style={s.cardsGrid}>
              {tasks.map((task, i) => {
                const ns = notionStatus[i] || {};
                const pStyle = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE['Medium ⏳'];
                const isSent = ns.success === true;
                return (
                  <div key={i} style={{ ...s.taskCard, ...(isSent ? s.taskCardSent : {}) }}>

                    {/* Card header */}
                    <div style={s.cardHeader}>
                      <div style={s.taskName}>{task.task_name || 'Untitled Task'}</div>
                      <div style={s.badgesRow}>
                        {task.priority && (
                          <span style={{
                            ...s.pill,
                            color: pStyle.color,
                            background: pStyle.bg,
                            border: `1px solid ${pStyle.border}`,
                          }}>
                            {task.priority}
                          </span>
                        )}
                        {task.category && (
                          <span style={s.pillBlue}>{task.category}</span>
                        )}
                        {task.vessel && (
                          <span style={s.pillVessel}>⚓ {task.vessel}</span>
                        )}
                        {task.type && (
                          <span style={s.pillType}>{TYPE_LABEL[task.type] || task.type}</span>
                        )}
                      </div>
                    </div>

                    {/* Summary */}
                    {task.summary && (
                      <div style={s.summaryText}>{task.summary}</div>
                    )}

                    {/* Meta row */}
                    <div style={s.metaRow}>
                      {task.sender && <span style={s.metaItem}>📧 {task.sender}</span>}
                      {task.due_date && <span style={s.metaItem}>📅 {task.due_date}</span>}
                      {task.key_people?.length > 0 && (
                        <span style={s.metaItem}>👥 {task.key_people.join(', ')}</span>
                      )}
                    </div>

                    {/* Notion send button */}
                    <div style={s.cardFooter}>
                      {!isSent && !ns.loading && !ns.error && (
                        <button
                          style={s.notionBtn}
                          onClick={() => sendTaskToNotion(task, i)}
                        >
                          📤 Send to Notion
                        </button>
                      )}
                      {ns.loading && (
                        <span style={s.notionSending}>⏳ Sending…</span>
                      )}
                      {isSent && (
                        <div style={s.notionSent}>
                          <span>✓ Sent to Notion</span>
                          {ns.url && (
                            <a href={ns.url} target="_blank" rel="noreferrer" style={s.notionLink}>
                              Open ↗
                            </a>
                          )}
                        </div>
                      )}
                      {ns.error && !isSent && (
                        <div style={s.notionError}>
                          <span>✗ {ns.error}</span>
                          <button style={s.retryBtn} onClick={() => sendTaskToNotion(task, i)}>
                            Retry
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)' },

  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex', alignItems: 'center', gap: '20px',
  },
  backBtn: {
    background: 'transparent', border: 'none', color: 'var(--grey)', cursor: 'pointer', fontSize: '13px',
  },
  headerTitle: { fontSize: '16px', fontWeight: '700', flex: 1 },
  headerBadge: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--gold)',
    border: '1px solid var(--gold)', padding: '4px 10px', borderRadius: '4px',
  },

  main: { padding: '40px', maxWidth: '1200px', margin: '0 auto' },

  // ── Upload ──
  uploadLayout: { display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '860px', margin: '0 auto' },

  dropZone: {
    background: 'var(--navy-mid)', border: '2px dashed var(--navy-light)',
    borderRadius: '14px', padding: '60px 40px', textAlign: 'center',
    cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s',
  },
  dropZoneActive: {
    borderColor: 'var(--gold)', background: 'rgba(201,168,76,0.05)',
  },
  dropIcon: { fontSize: '40px', marginBottom: '14px' },
  dropTitle: { fontSize: '16px', fontWeight: '700', marginBottom: '6px' },
  dropSub: { fontSize: '13px', color: 'var(--grey)' },

  badgeRow: { display: 'flex', gap: '10px' },
  badge: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '6px', padding: '6px 14px', fontSize: '12px',
    fontFamily: 'var(--font-mono)', color: 'var(--grey-light)',
  },
  badgeGold: { color: 'var(--gold)', borderColor: 'rgba(201,168,76,0.4)' },

  error: {
    background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#ff6b7a',
  },

  tableWrap: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', overflow: 'hidden',
  },
  tableLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '2px',
    color: 'var(--grey)', padding: '14px 20px 10px',
    borderBottom: '1px solid var(--navy-border)',
  },
  table: {},
  tableHeader: {
    display: 'grid', gridTemplateColumns: '2fr 3fr 1.2fr 1fr',
    padding: '10px 20px', fontSize: '10px', fontWeight: '600',
    letterSpacing: '1.5px', color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)',
    background: 'rgba(255,255,255,0.02)',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '2fr 3fr 1.2fr 1fr',
    padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)',
    fontSize: '12px', alignItems: 'center',
  },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--grey-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  subjectCell: { fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '10px' },
  statusBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
    fontSize: '10px', fontFamily: 'var(--font-mono)', fontWeight: '600',
  },
  statusReview: { color: '#4caf7d', background: 'rgba(76,175,125,0.12)', border: '1px solid rgba(76,175,125,0.3)' },
  statusOther: { color: 'var(--grey)', background: 'transparent', border: '1px solid var(--navy-light)' },

  analyzeBtn: {
    width: '100%', background: 'var(--red)', border: 'none', borderRadius: '10px',
    padding: '16px', color: 'white', fontSize: '14px', fontWeight: '700',
    letterSpacing: '1px', cursor: 'pointer',
  },

  analyzingBox: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '40px', textAlign: 'center',
  },
  analyzingIcon: { fontSize: '36px', marginBottom: '12px' },
  analyzingText: { fontSize: '15px', fontWeight: '600', marginBottom: '6px' },
  analyzingSub: { fontSize: '12px', color: 'var(--grey)' },

  // ── Results ──
  resultsLayout: { display: 'flex', flexDirection: 'column', gap: '20px' },

  resultsTopBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: '12px',
  },
  summaryRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  summaryChip: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '6px', padding: '6px 12px', fontSize: '12px',
    fontFamily: 'var(--font-mono)',
  },
  chipRed: { color: '#ff6b7a', borderColor: 'rgba(255,77,77,0.3)' },
  chipGold: { color: 'var(--gold)', borderColor: 'rgba(201,168,76,0.3)' },
  chipBlue: { color: '#60a5fa', borderColor: 'rgba(96,165,250,0.3)' },
  chipGreen: { color: '#4caf7d', borderColor: 'rgba(76,175,125,0.3)' },

  topBtnRow: { display: 'flex', gap: '10px' },
  resetBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '8px 16px', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  sendAllBtn: {
    background: 'var(--red)', border: 'none', borderRadius: '8px',
    padding: '8px 18px', color: 'white', fontSize: '12px',
    fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px',
  },

  emptyResults: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '60px', textAlign: 'center',
  },
  emptyIcon: { fontSize: '40px', marginBottom: '14px' },
  emptyText: { fontSize: '15px', fontWeight: '600', marginBottom: '6px' },
  emptySub: { fontSize: '12px', color: 'var(--grey)' },

  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))',
    gap: '16px',
  },

  taskCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    transition: 'opacity 0.3s',
  },
  taskCardSent: { opacity: 0.55 },

  cardHeader: { display: 'flex', flexDirection: 'column', gap: '8px' },
  taskName: { fontSize: '15px', fontWeight: '700', lineHeight: '1.3', color: 'var(--white)' },
  badgesRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },

  pill: {
    display: 'inline-block', padding: '3px 9px', borderRadius: '4px',
    fontSize: '11px', fontWeight: '600', fontFamily: 'var(--font-mono)',
  },
  pillBlue: {
    display: 'inline-block', padding: '3px 9px', borderRadius: '4px',
    fontSize: '11px', fontWeight: '600', fontFamily: 'var(--font-mono)',
    color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)',
  },
  pillVessel: {
    display: 'inline-block', padding: '3px 9px', borderRadius: '4px',
    fontSize: '11px', fontWeight: '600', fontFamily: 'var(--font-mono)',
    color: '#4caf7d', background: 'rgba(76,175,125,0.1)', border: '1px solid rgba(76,175,125,0.3)',
  },
  pillType: {
    display: 'inline-block', padding: '3px 9px', borderRadius: '4px',
    fontSize: '11px', fontFamily: 'var(--font-mono)',
    color: 'var(--grey)', background: 'transparent', border: '1px solid var(--navy-light)',
  },

  summaryText: { fontSize: '13px', color: 'var(--grey-light)', lineHeight: '1.6' },

  metaRow: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
  metaItem: {
    fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px',
  },

  cardFooter: { marginTop: 'auto', paddingTop: '4px' },
  notionBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey-light)', padding: '7px 14px', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  notionSending: { fontSize: '12px', color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  notionSent: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '12px', color: '#4caf7d', fontFamily: 'var(--font-mono)',
  },
  notionLink: {
    color: '#60a5fa', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontSize: '12px',
  },
  notionError: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '12px', color: '#ff6b7a', fontFamily: 'var(--font-mono)',
  },
  retryBtn: {
    background: 'transparent', border: '1px solid rgba(255,107,122,0.4)',
    color: '#ff6b7a', padding: '3px 10px', borderRadius: '4px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  guideBtn: { background:'transparent', border:'1px solid var(--navy-light)', color:'var(--grey)', padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  modal: { background:'#111d2e', border:'1px solid #1e2d40', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'82vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.6)' },
  modalHeader: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'22px 26px 18px', borderBottom:'1px solid #1e2d40', flexShrink:0 },
  modalTitle: { fontSize:15, fontWeight:700, color:'#e8edf4', marginBottom:4 },
  modalSub: { fontSize:11, fontFamily:'var(--font-mono)', color:'var(--gold)', letterSpacing:1 },
  modalClose: { background:'transparent', border:'none', color:'var(--grey)', fontSize:16, cursor:'pointer', padding:'0 0 0 16px', lineHeight:1 },
  modalBody: { overflowY:'auto', padding:'22px 26px', display:'flex', flexDirection:'column', gap:22 },
  gSection: {},
  gTitle: { fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--gold)', marginBottom:10, textTransform:'uppercase' },
  gText: { fontSize:12, color:'#8a9ab0', lineHeight:1.7 },
  hl: { color:'#e8edf4' },
  step: { display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 },
  stepN: { flexShrink:0, width:26, height:26, borderRadius:'50%', background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.3)', color:'var(--gold)', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-mono)' },
  stepT: { fontSize:13, fontWeight:600, color:'#e8edf4', marginBottom:3 },
  noteList: { display:'flex', flexDirection:'column', gap:7 },
  noteItem: { display:'flex', gap:8, fontSize:12, color:'#8a9ab0', lineHeight:1.6 },
  noteDot: { color:'var(--gold)', flexShrink:0 },
};
